import { type Control, type FieldPath } from 'react-hook-form';

import { ControlledSelectV2 } from '@packages/components/controls/controlled-select-v2/controlled-select-v2';
import type { GridProps } from '@packages/fenrir/components';
import { Grid, Skeleton } from '@packages/fenrir/components';
import { Stack } from '@packages/fenrir/components/stack';
import { Typography } from '@packages/fenrir/components/typography';
import { useFeatureFlag } from '@packages/platform/feature-flags/use-feature-flag';
import type { Option } from '@packages/utils/create-options/create-options';

import type { ScannerDetailsForm } from '../../../../../types';

export type ScannerConfigurationDropdownProps = {
  control: Control<ScannerDetailsForm>;
  controlName: FieldPath<ScannerDetailsForm>;
  label: string;
  description: string;
  placeholder: string;
  options: Option[];
  isPending: boolean;
  isDisabled?: boolean;
  gridSize?: GridProps['size'];
};

export const ScannerConfigurationDropdown: React.FC<
  ScannerConfigurationDropdownProps
> = ({
  control,
  controlName,
  label,
  description,
  placeholder,
  options,
  isPending,
  isDisabled = false,
  gridSize = { xs: 12, lg: 6, xl: 4 },
}) => {
  const [configureActivateFF] = useFeatureFlag('SCANNER_CONFIGURE_ACTIVATE');
  return (
    <Grid size={gridSize}>
      <Stack
        spacing={2}
        padding={configureActivateFF ? 2 : undefined}
        gap={configureActivateFF ? 1 : undefined}
        sx={{ height: '100%', justifyContent: 'space-between' }}
      >
        <Stack spacing={1}>
          <Typography variant="subtitle1">{label}</Typography>
          <Typography variant="caption">{description}</Typography>
        </Stack>

        {isPending ? (
          <Skeleton
            animation="wave"
            width="100%"
            height={40}
            title="scanner-status-skeleton"
          />
        ) : (
          <ControlledSelectV2
            controllerProps={{
              control,
              name: controlName,
            }}
            formLabelProps={{
              hidden: true,
            }}
            label={controlName}
            options={options}
            disabled={isDisabled}
            placeholder={placeholder}
          />
        )}
      </Stack>
    </Grid>
  );
};
